// AudioService.js - SIMPLIFIED VERSION (Startup Sound Only + Hume Player Integration)
class AudioService {
  constructor() {
    // State tracking
    this.soundEnabled = true;
    this.isInitializing = false;
    
    // Audio elements for custom sounds
    this.startupAudio = null;
    
    // State tracking for custom sounds
    this.startupSoundPlaying = false;
    
    // State update callbacks for UI integration
    this.onStateChange = null;
    
    // Sound URLs
    this.STARTUP_SOUND_URL = '/sound/calling2.mp3';
  }

  // Set callback for state changes (to update React component state)
  setStateChangeCallback(callback) {
    this.onStateChange = callback;
  }

  // Notify UI of state changes
  notifyStateChange() {
    if (this.onStateChange) {
      this.onStateChange({
        startupSoundPlaying: this.startupSoundPlaying,
        soundEnabled: this.soundEnabled,
        isInitializing: this.isInitializing
      });
    }
  }

  // Initialize startup audio
  initializeStartupAudio() {
    if (!this.startupAudio) {
      this.startupAudio = new Audio(this.STARTUP_SOUND_URL);
      this.startupAudio.preload = 'auto';
      this.startupAudio.volume = 0.7;
      
      this.startupAudio.oncanplaythrough = () => {
        console.log('🔊 Startup sound loaded and ready');
      };
      
      this.startupAudio.onerror = (e) => {
        console.error('❌ Failed to load startup sound:', e);
        this.startupSoundPlaying = false;
        this.notifyStateChange();
      };
      
      this.startupAudio.onended = () => {
        console.log('🔊 Startup sound finished playing');
        this.startupSoundPlaying = false;
        this.notifyStateChange();
      };

      this.startupAudio.onpause = () => {
        console.log('🔊 Startup sound paused');
        this.startupSoundPlaying = false;
        this.notifyStateChange();
      };
    }
  }

  // Play startup sound with delay - shows "Calling" indicator
  async playStartupSound(delayMs = 600) {
    if (!this.soundEnabled) return Promise.resolve();
    
    return new Promise((resolve, reject) => {
      console.log(`🔊 Starting calling sequence - waiting ${delayMs}ms before startup sound...`);
      
      // Immediately set calling state to show "Calling" indicator
      this.startupSoundPlaying = true;
      this.notifyStateChange();
      
      setTimeout(() => {
        if (!this.startupAudio) {
          this.initializeStartupAudio();
        }
        
        if (this.startupAudio) {
          // Override the onended handler for this specific call
          this.startupAudio.onended = () => {
            console.log('🔊 Startup sound finished playing - ending calling state');
            this.startupSoundPlaying = false;
            this.notifyStateChange();
            resolve();
          };
          
          this.startupAudio.onerror = (e) => {
            console.error('❌ Startup sound playback error:', e);
            this.startupSoundPlaying = false;
            this.notifyStateChange();
            reject(e);
          };
          
          const playPromise = this.startupAudio.play();
          
          if (playPromise !== undefined) {
            playPromise
              .then(() => {
                console.log('🔊 Startup sound started playing - calling indicator should be visible');
              })
              .catch((error) => {
                console.error('❌ Startup sound play failed:', error);
                this.startupSoundPlaying = false;
                this.notifyStateChange();
                reject(error);
              });
          }
        } else {
          console.warn('⚠️ Startup audio not initialized, ending calling state');
          this.startupSoundPlaying = false;
          this.notifyStateChange();
          resolve();
        }
      }, delayMs);
    });
  }

  // Toggle sound on/off
  toggleSound() {
    this.soundEnabled = !this.soundEnabled;
    
    if (!this.soundEnabled) {
      // If turning sound off, stop startup audio
      this.stopAllAudio();
    }
    
    this.notifyStateChange();
    return this.soundEnabled;
  }

  // Methods for handling EVI message flow (simplified)
  resetForNewUserTurn() {
    // Just a placeholder - EVI audio is now handled by Hume player
    console.log('🔊 User turn started - EVI audio managed by Hume player');
  }

  handleAssistantMessage() {
    // Just a placeholder - EVI audio is now handled by Hume player
    console.log('🔊 Assistant message - EVI audio managed by Hume player');
  }

  handleAssistantEnd() {
    // Just a placeholder - EVI audio is now handled by Hume player
    console.log('🔊 Assistant ended - EVI audio managed by Hume player');
  }

  handleUserInterruption() {
    // Just a placeholder - EVI audio is now handled by Hume player
    console.log('🔊 User interruption - EVI audio managed by Hume player');
  }

  // Stop all audio (only startup sound now)
  stopAllAudio() {
    console.log('🔊 Stopping all AudioService audio');
    
    // Stop startup audio
    if (this.startupAudio && this.startupSoundPlaying) {
      this.startupAudio.pause();
      this.startupAudio.currentTime = 0;
      this.startupSoundPlaying = false;
    }
    
    this.notifyStateChange();
  }

  // Force stop calling state (for manual stop)
  stopCallingState() {
    if (this.startupAudio && this.startupSoundPlaying) {
      this.startupAudio.pause();
      this.startupAudio.currentTime = 0;
    }
    this.startupSoundPlaying = false;
    this.notifyStateChange();
    console.log('🔊 Calling state manually stopped');
  }

  // Initialize all audio
  initializeAllAudio() {
    this.initializeStartupAudio();
  }

  // Get audio states for UI
  getAudioStates() {
    return {
      soundEnabled: this.soundEnabled,
      startupSoundPlaying: this.startupSoundPlaying,
      isInitializing: this.isInitializing
    };
  }

  // Enhanced cleanup method
  cleanup() {
    this.stopAllAudio();
    
    if (this.startupAudio) {
      this.startupAudio.pause();
      this.startupAudio = null;
    }
    
    console.log('✅ AudioService cleanup completed');
  }
}

export default AudioService;